import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { NavigateHeader } from '@/components/atoms/navigate-header'
import { Layout } from '@/components/template/layout'
import { LoadingFull } from '@/components/atoms/loading-full'
import { fetchSpaceReservationDetail, clearReservation } from '@/features/space/space-reservation-detail-slice'
import { cancelSpaceReservation } from '@/features/space/space-reservation-cancel-slice'
import { SpaceReservationCancelConfirmModal } from '@/components/organisms/modal/space-reservation-cancel-confirm-modal'
import { SpaceReservationCancelCompleteModal } from '@/components/organisms/modal/space-reservation-cancel-complete-modal'
import { Button } from '@/components/atoms/button'
import { errorHandler } from '@/libs/errors'
import { pcScreenStyle } from '@/styles/common'
import { CUSTOM_BLOCK } from '@/components/template/custom-design-style'
import { buildCancelPolicyMessage } from './space-reservation-display'
import { SpaceReservationKeyDisplayValue } from './space-reservation-key-display'
import { usePickByLanguage } from '@/hooks/use-pick-by-language'
import { css } from '@emotion/react'
import dayjs from 'dayjs'

export const SpaceReservationDetail: React.FC = () => {
  const { t } = useTranslation()
  const pickByLanguage = usePickByLanguage()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const reservation = useAppSelector(state => state.spaceReservationDetail?.reservation)
  const detailLoading = useAppSelector(state => state.spaceReservationDetail?.loading ?? false)
  const cancelLoading = useAppSelector(state => state.spaceReservationCancel?.loading ?? false)
  const design = useAppSelector(state => state.design)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [showCancelCompleteModal, setShowCancelCompleteModal] = useState(false)

  useEffect(() => {
    if (id) {
      const fetchDetail = async () => {
        try {
          await dispatch(fetchSpaceReservationDetail(id)).unwrap()
        } catch (error) {
          errorHandler({ error })
        }
      }
      fetchDetail()
    }

    return () => {
      dispatch(clearReservation())
    }
  }, [id, dispatch])

  const handleCancel = async () => {
    if (!id || cancelLoading) return

    try {
      await dispatch(cancelSpaceReservation(id)).unwrap()
      setShowCancelModal(false)
      setShowCancelCompleteModal(true)
    } catch (error) {
      errorHandler({ error })
    }
  }

  const handleCloseCancelComplete = () => {
    setShowCancelCompleteModal(false)
    navigate('/mypage/space-reservations')
  }

  const cancelPolicyMessage = useMemo(
    () => (reservation ? buildCancelPolicyMessage(reservation.cancelPolicy, t) : ''),
    [reservation, t]
  )

  if (!reservation && !detailLoading) {
    return (
      <Layout>
        <NavigateHeader title={t('Space Reservations')} />
      </Layout>
    )
  }

  const isAnyLoading = detailLoading || cancelLoading

  if (!reservation) {
    return (
      <Layout>
        <NavigateHeader title={t('Space Reservations')} />
        <LoadingFull loading={isAnyLoading} />
      </Layout>
    )
  }

  return (
    <Layout>
      <NavigateHeader title={t('Space Reservations')} />
      <div css={pcScreenStyle}>
        <section css={cardSection} className={CUSTOM_BLOCK}>
          <div css={[spaceNameRow, { borderBottomColor: design?.background.backgroundColor }]}>
            <h2 css={[spaceNameTitle, { color: design?.text.textColor }]}>{pickByLanguage(reservation.spaceTitle)}</h2>
          </div>

          <div css={[detailRow, { borderBottomColor: design?.background.backgroundColor }]}>
            <span>{t('Usage Date Time')}</span>
            <span>
              {dayjs(reservation.usageFrom).format('YYYY/MM/DD HH:mm')}〜{dayjs(reservation.usageTo).format('HH:mm')}
            </span>
          </div>

          {reservation.keyNumber != null && reservation.keyNumber.trim() !== '' && (
            <div css={[detailRow, { borderBottomColor: design?.background.backgroundColor }]}>
              <span>{t('Key Number')}</span>
              <span>
                <SpaceReservationKeyDisplayValue
                  keyNumber={reservation.keyNumber}
                  hasQrCode={reservation.hasQrCode}
                  universalAccessKeyUrl={reservation.universalAccessKeyUrl ?? null}
                  linkColor={design?.text.linkColor ?? '#0066cc'}
                  t={t}
                />
              </span>
            </div>
          )}

          <div css={[detailRow, { borderBottomColor: design?.background.backgroundColor }]}>
            <span>{t('Price')}</span>
            <span>¥{reservation.price.toLocaleString()}</span>
          </div>

          <div css={detailRowLast}>
            <span>{t('Payment Method')}</span>
            <span>{reservation.paymentMethod}</span>
          </div>
        </section>

        <section css={policySection}>
          <div
            css={[policyBox, { backgroundColor: design?.background.cardColor, borderColor: design?.background.backgroundColor }]}
          >
            <h3 css={[policyTitle, { color: design?.text.textColor }]}>{t('Cancel Policy')}</h3>
            <p css={[policyText, { color: design?.text.textColor }]}>{cancelPolicyMessage}</p>
          </div>
        </section>

        <section css={buttonSection}>
          {(reservation.canCancel || reservation.isPastUsageStart) && (
            <div css={buttonWrap}>
              <Button
                text={t('Cancel this reservation')}
                onClick={() => !reservation.isPastUsageStart && setShowCancelModal(true)}
                disabled={cancelLoading || reservation.isPastUsageStart}
                loading={cancelLoading}
                buttonCss={cancelButtonHeight}
              />
              {reservation.isPastUsageStart && (
                <p css={usagePastMessageStyle} style={{ color: design?.text.textColor }}>
                  {t('This reservation has passed the usage start time.')}
                </p>
              )}
            </div>
          )}
        </section>
      </div>

      <LoadingFull loading={cancelLoading} />
      <SpaceReservationCancelConfirmModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancel}
        reservation={reservation}
        loading={cancelLoading}
      />
      <SpaceReservationCancelCompleteModal
        isOpen={showCancelCompleteModal}
        onClose={handleCloseCancelComplete}
      />
    </Layout>
  )
}

const cardSection = css({
  marginTop: '1rem',
  padding: 0,
  overflow: 'hidden',
})

const spaceNameRow = css({
  padding: '24px 32px',
  borderBottom: '1px solid',
})

const spaceNameTitle = css({
  margin: 0,
  fontSize: '1.25rem',
  fontWeight: 'bold',
})

const detailRowBase = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1rem 32px',
  fontSize: '14px',
}

const detailRow = css({
  ...detailRowBase,
  borderBottom: '1px solid',
})

const detailRowLast = css({
  ...detailRowBase,
  paddingBottom: '16px',
  marginBottom: '16px',
})

const policySection = css({
  marginTop: '16px',
  padding: '0 1rem',
})

const policyBox = css({
  padding: '16px',
  borderRadius: '6px',
  border: '1px solid',
  opacity: 1,
})

const policyTitle = css({
  margin: '0 0 0.5rem',
  fontSize: '12px',
  lineHeight: '27px',
  fontWeight: 'bold',
  letterSpacing: '0.6px',
})

const policyText = css({
  margin: 0,
  fontSize: '12px',
  lineHeight: '21px',
  letterSpacing: '1.2px',
})

const buttonSection = css({
  marginTop: '1.5rem',
  padding: '0 1rem 2.5rem',
})

const buttonWrap = css({
  width: '100%',
  padding: '0.5rem 0',
})

const cancelButtonHeight = css({
  '& button': {
    height: '48px',
  },
})

const usagePastMessageStyle = css({
  width: '100%',
  margin: '0.75rem 0 0',
  padding: 0,
  fontSize: '14px',
  fontWeight: 600,
  textAlign: 'center',
})
